/**
 * Card Component
 *
 * Reusable card container component for displaying content in a visually
 * distinct section with consistent styling.
 */

import { HTMLAttributes, ReactNode } from 'react';
import './common.css';

export type CardVariant = 'default' | 'outlined' | 'elevated';
export type CardPadding = 'none' | 'small' | 'medium' | 'large';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** The variant style of the card */
  variant?: CardVariant;
  /** The padding size inside the card */
  padding?: CardPadding;
  /** Whether the card is clickable (shows hover effect) */
  clickable?: boolean;
  /** Card header content */
  header?: ReactNode;
  /** Card footer content */
  footer?: ReactNode;
  /** Card body content */
  children: ReactNode;
}

export function Card({
  variant = 'default',
  padding = 'medium',
  clickable = false,
  header,
  footer,
  children,
  className = '',
  ...props
}: CardProps) {
  const cardClasses = [
    'card',
    `card-${variant}`,
    `card-padding-${padding}`,
    clickable ? 'card-clickable' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClasses} {...props}>
      {header && <div className="card-header">{header}</div>}
      <div className="card-body">{children}</div>
      {footer && <div className="card-footer">{footer}</div>}
    </div>
  );
}

export default Card;
